import React, { useState, useEffect, useCallback } from "react";
import { Flex } from "@jsq/ds/core";
import { ChoiceSelector } from "../common/ChoiceSelector";
import { NO, YES } from "../common/constants";
import { DefaultModal } from "../common/DefaultModal";
import { EntityTypeSurveyProps, Question } from "./types";

/**
 * EntityTypeSurvey Component
 *
 * This component renders a dynamic survey with Yes/No questions where:
 * - Questions are displayed conditionally based on previous answers
 * - Only the first question and questions following a "No" answer are displayed
 * - Changing an answer from "No" to "Yes" clears all subsequent questions
 * - Changing previously answered questions (except last question or Yes→No changes)
 *   shows a warning that subsequent answers will be lost
 *
 * @param {EntityTypeSurveyProps} props - The component props
 * @returns {React.ReactElement} The rendered component
 */
export const EntityTypeSurvey: React.FC<EntityTypeSurveyProps> = ({
  questions,
  onSurveyChange,
}: EntityTypeSurveyProps) => {
  const initialValuePresent = questions.some((question) => !!question.answer);
  // Stores the current state of all questions with their answers
  const [questionsState, setQuestionsState] = useState(
    questions
      .map((q, index) =>
        q.answer === NO &&
        index > 0 &&
        questions.slice(0, index).some((prevQ) => prevQ.answer === YES)
          ? { ...q, answer: "" }
          : q,
      )
      .map((question) => ({ ...question })),
  );

  // Controls visibility of the warning when changing previous answers
  const [showWarning, setShowWarning] = useState(false);

  // Stores information about a pending answer change that requires confirmation
  const [pendingChange, setPendingChange] = useState<{
    questionId: string;
    newAnswer: string;
  } | null>(null);

  // Add state to track if warning has been shown
  const [hasShownWarning, setHasShownWarning] = useState(false);

  // Notify parent component when questions state changes
  useEffect(() => {
    if (onSurveyChange) {
      onSurveyChange(questionsState);
    }
  }, [questionsState]);

  /**
   * Applies an answer change to the question state
   */
  const applyAnswerChange = useCallback((questionId: string, answer: string) => {
    setQuestionsState((prevState) => {
      const questionIndex = prevState.findIndex((q) => q.id === questionId);
      const currentQuestion = prevState[questionIndex];

      // Create a new array with the updated answer for the current question
      let updatedQuestions = prevState.map((q) => (q.id === questionId ? { ...q, answer } : q));

      // If changing from "No" to "Yes", clear all subsequent questions
      if (currentQuestion.answer === NO && answer === YES) {
        updatedQuestions = updatedQuestions.map((q, index) =>
          index > questionIndex ? { ...q, answer: "" } : q,
        );
      }

      return updatedQuestions;
    });
  }, []);

  /**
   * Handles when a user selects an answer for a question
   */
  const handleAnswerChange = useCallback(
    (question: Question, answer: string) => {
      if (initialValuePresent && !hasShownWarning) {
        setShowWarning(true);
        setPendingChange({ questionId: question.id, newAnswer: answer });
      } else {
        applyAnswerChange(question.id, answer);
      }
    },
    [initialValuePresent, hasShownWarning, applyAnswerChange],
  );

  /**
   * Handles the user's response to the warning dialog
   */
  const warningCallback = useCallback(
    (accept: boolean) => {
      if (pendingChange && accept) {
        applyAnswerChange(pendingChange.questionId, pendingChange.newAnswer);
        setHasShownWarning(true);
      }
      setShowWarning(false);
      setPendingChange(null);
    },
    [pendingChange, applyAnswerChange],
  );

  return (
    <Flex flexDirection="column">
      {/* Render each question based on conditional logic */}
      {questionsState.map((question, index) => (
        <Flex key={question.id}>
          {/* Only show the first question or questions ALL previous answers have a "No" answer */}
          {index === 0 ||
          (questionsState.slice(0, index).every((q) => q.answer === NO) && index > 0) ? (
            <ChoiceSelector
              option={question}
              answers={[YES, NO]}
              onChange={(question, answer) => handleAnswerChange(question, answer)}
            />
          ) : null}
        </Flex>
      ))}
      {/* Show warning dialog when attempting to change previous answers */}
      <DefaultModal
        show={showWarning}
        title="Warning"
        body={
          <>
            <p>
              Changing this answer may delete any information or documents submitted within the
              Identification step that are no longer applicable.
            </p>
            <p>
              Information or documents submitted outside the Identification step will be
              unaffected.
            </p>
          </>
        }
        negBtnText="Cancel"
        onClose={() => warningCallback(false)}
        onConfirm={() => warningCallback(true)}
      />
    </Flex>
  );
};
